import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  IconButton,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  UnorderedList,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useFigmaDownload } from 'hooks/useFigmaDownload';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import DevSandbox from 'components/DevSandbox';
import { formatCode } from 'components/DevSandbox/CodeEditor/CodeEditor';
import EditorSettingsButton, { ShareButton } from 'components/DevSandbox/EditorSettingsButton';
import { MerlinToggleButton } from 'components/DevSandbox/Merlin';
import BrowserTab from 'components/DevSandbox/PaneTabs/BrowserTab';
import DiscussionTab from 'components/DevSandbox/PaneTabs/Discussion/DiscussionTab';
import RightSideActions from 'components/DevSandbox/RightSideActions';
import { HTML_LEARN_TEMPLATE } from 'components/DevSandbox/templates';
import Icon from 'components/Icon';
import Link from 'components/Link';
import { UnlockFigmaModal } from 'components/Modal/UnlockFigmaModal';
import ProjectElement from 'components/ProjectElement';
import { ProtectedElement } from 'components/ProtectedElement';
import Text from 'components/Text';
import { StatusIconMapper, StatusTextMapper } from 'features/practice-problems/PracticeProblems';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { Support_Type_Enum, User_Challenge_Status_Enum } from 'types/databaseEnums';
import { Challenge } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

import ChallengesDrawer from './ChallengesDrawer';

interface IChallengePlaygroundProps {
  challenge: Challenge;
}

const ChallengePlayground = ({
  challenge: {
    id,
    is_figma_downloaded,
    title,
    status,
    preview_url,
    screen_name,
    screenshot_url,
    discussion_count,
  },
}: IChallengePlaygroundProps) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [codeFrameUrl, setCodeFrameUrl] = useState<string>('');
  const loadTimeoutRef = useRef<any | null>(null);

  const { id: userId, isFree } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const { isSmallScreen } = useMediaQueries();

  const { isDownloadingFigmaDesign, onFigmaDownload } = useFigmaDownload({
    screenName: screen_name,
    type: 'challenge',
    entityId: id,
  });

  useEffect(() => {
    loadTimeoutRef.current = setTimeout(() => {
      setIsLoaded(true);
    }, 500);

    return () => {
      clearTimeout(loadTimeoutRef.current);
    };
  }, []);

  const codeEditorRef = useRef<any>(null);

  const queryClient = useQueryClient();

  const figmaModal = useDisclosure();

  const navigate = useNavigate();

  const hasTabInUrl =
    location.pathname.includes('browser') ||
    location.pathname.includes('instructions') ||
    location.pathname.includes('discussions');
  const tabScreenName = location.pathname.split('/').pop();
  const browserUrl = hasTabInUrl
    ? window.location.href.replace(`/${tabScreenName}`, '')
    : window.location.href;

  const searchParamsMode = searchParams.get('mode');
  const mode = searchParamsMode === 'full-page' ? 'full-page' : 'interactive';

  const markdownInstructions = `
  # User Story
  As a user, I want to create a solution that reflects the given design, adding unique interactivity and flair to enhance the standard layout.

  ## Acceptance Criteria
  - The solution accurately mirrors the layout and style of the provided screenshot using HTML, CSS, and JavaScript.
  - Enhance the solution with interactivity to improve the user experience beyond the static design.
  - Ensure the implementation is functional and of high quality.
  - Use the assets available in this Code Editor, including starter files, Figma design, etc.
  - Discuss with the community, review their solutions, and leverage AI-powered actions (chatting, generating solutions, etc.).
  - Aim to complete the challenge without perfection and with your unique variation.
  `;

  return (
    <>
      <DevSandbox
        ref={codeEditorRef}
        id={`challenge-${screen_name}-${userId}`}
        oldKey={preview_url}
        browserUrl={browserUrl}
        title={title}
        mode={mode}
        entityType="challenge"
        entityId={screen_name}
        merlinConfig={{
          entityName: title,
          entityType: 'challenge',
          designScreenshotUrl: screenshot_url,
        }}
        navigation={{
          leftSide: <ChallengesDrawer />,
          header: (
            <HStack spacing={2}>
              <MerlinToggleButton />
              <HStack
                data-tour="submit-button"
                spacing={0}
                borderRadius="md"
                background="linear-gradient(90deg, rgba(159, 122, 234, 0.87) 0%, rgba(237, 137, 54, 0.87) 100%)"
              >
                <ProtectedElement
                  scheme="ghost"
                  leftIcon={<Icon type="SubmitCode" />}
                  onClick={() => {
                    navigate('submit');
                  }}
                  sx={{
                    borderRightRadius: 0,
                    color: 'white',
                    _hover: { bg: 'whiteAlpha.200' },
                  }}
                >
                  Submit
                </ProtectedElement>
                <Menu placement="bottom-end">
                  <MenuButton
                    as={IconButton}
                    icon={<ChevronDownIcon />}
                    size="md"
                    aria-label="Submit options"
                    variant="ghost"
                    color="white"
                    sx={{
                      borderLeftRadius: 0,
                      borderLeft: '1px solid',
                      borderColor: 'whiteAlpha.300',
                      minW: '36px',
                      _hover: { bg: 'whiteAlpha.200' },
                      _active: { bg: 'whiteAlpha.300' },
                    }}
                  />
                  <Portal>
                    <MenuList bg="gray.800" borderColor="whiteAlpha.200" minW="280px" py={0} overflow="hidden" zIndex="tooltip">
                      <MenuItem
                        bg="gray.800"
                        _hover={{ bg: 'whiteAlpha.100' }}
                        onClick={() => navigate('submit')}
                        py={3}
                        px={4}
                      >
                        <HStack spacing={3}>
                          <Box color="purple.300">
                            <Icon type="SubmitCode" />
                          </Box>
                          <VStack alignItems="flex-start" spacing={0}>
                            <Text fontWeight="semibold">Code Editor</Text>
                            <Text fontSize="micro" opacity="secondary" fontWeight="regular">
                              Submit code written in the built-in editor
                            </Text>
                          </VStack>
                        </HStack>
                      </MenuItem>
                      <MenuItem
                        bg="gray.800"
                        _hover={{ bg: 'whiteAlpha.100' }}
                        onClick={() => navigate('submit?via=github')}
                        py={3}
                        px={4}
                        borderTop="1px solid"
                        borderColor="whiteAlpha.100"
                      >
                        <HStack spacing={3}>
                          <Box>
                            <Icon type="Github" size={20} />
                          </Box>
                          <VStack alignItems="flex-start" spacing={0}>
                            <Text fontWeight="semibold">Submit via GitHub</Text>
                            <Text fontSize="micro" opacity="secondary" fontWeight="regular">
                              Link your deployed website and repository
                            </Text>
                          </VStack>
                        </HStack>
                      </MenuItem>
                    </MenuList>
                  </Portal>
                </Menu>
              </HStack>
            </HStack>
          ),
          rightSide: (
            <RightSideActions
              leftActions={
                !isSmallScreen ? (
                  <HStack spacing={2}>
                    <EditorSettingsButton />
                    <ShareButton />
                  </HStack>
                ) : undefined
              }
            />
          ),
        }}
        codeTabs={{
          html: formatCode(
            HTML_LEARN_TEMPLATE(
              'https://github.com/elise-bigdevsoon/big-dev-soon-challenge-template',
              title,
              'challenge'
            ),
            0
          ),
          css: '',
          js: '',
        }}
        callbacks={{
          onCodeFrameUrlChange: (codeFrameUrl) => {
            setCodeFrameUrl(codeFrameUrl);
          },
        }}
        paneTabs={{
          tabs: [
            {
              screenName: 'browser',
              title: 'Browser',
              icon: <Icon type="Browser" size={16} />,
              content: (
                <BrowserTab
                  browserUrl={browserUrl}
                  codeFrameUrl={codeFrameUrl}
                  onCodeFrameUrlChange={codeEditorRef.current?.onCodeFrameUrlChange}
                  designScreenshotUrl={screenshot_url}
                  showOverlayControls
                  scoreConfig={{
                    designScreenshotUrl: screenshot_url,
                    screenshotWidth: 1440,
                    screenshotHeight: 1080,
                  }}
                />
              ),
            },
            {
              screenName: 'instructions',
              title: 'Instructions',
              icon: <Icon type="CodeInstructions" size={16} />,
              content: (
                <VStack alignItems="flex-start" p={4} spacing={4}>
                  <VStack alignItems="flex-start" width="100%">
                    <HStack justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                      <HStack>
                        <Text fontSize="xxLarge" fontWeight="bold">
                          {title}
                        </Text>
                        <ProjectElement
                          type={
                            status === User_Challenge_Status_Enum.Completed
                              ? 'experienceCompleted'
                              : 'experience'
                          }
                          experience={25}
                        />
                      </HStack>
                      <HStack>
                        {StatusIconMapper[status]}
                        <Text fontSize="small">{StatusTextMapper[status]}</Text>
                      </HStack>
                    </HStack>
                  </VStack>
                  <ReactMarkdown
                    components={{
                      h1: ({ node, ...props }) => (
                        <HStack mt={5} mb={3}>
                          <Icon type="InstructionsUserStory" size={24} />
                          <Text fontSize="xLarge" {...props} />
                        </HStack>
                      ),
                      h2: ({ node, ...props }) => (
                        <HStack mt={5} mb={3}>
                          <Icon type="InstructionsAcceptanceCriteria" size={24} />
                          <Text fontSize="xLarge" {...props} />
                        </HStack>
                      ),
                      ul: ({ node, ...props }) => <UnorderedList spacing={2} pl={3} {...props} />,
                      li: ({ node, ...props }) => (
                        <ListItem lineHeight="26px" fontWeight="regular" {...props} />
                      ),
                      a: ({ node, ...props }) => <Link {...props} />,
                      p: ({ node, ...props }) => (
                        <Text lineHeight="26px" {...props} fontWeight="regular" />
                      ),
                    }}
                  >
                    {markdownInstructions}
                  </ReactMarkdown>
                </VStack>
              ),
            },
            {
              screenName: 'discussions',
              title: `${
                discussion_count > 0 ? `Discussions (${discussion_count})` : 'Discussions'
              }`,
              icon: <Icon type="Discussion" size={16} />,
              content: (
                <DiscussionTab
                  entityId={id}
                  type={Support_Type_Enum.Question}
                  refetch={() => {
                    queryClient.invalidateQueries({
                      queryKey: [TanstackQueryName.GetChallenge, screen_name],
                    });
                  }}
                />
              ),
            },
          ],
        }}
        footer={{
          assetsConfig: {
            showDesignTab: true,
            showExtractTextTab: true,
            showYourFilesTab: true,
            showPackagesTab: true,
            showToolsTab: false,
            designScreenshotUrl: screenshot_url,
            entityType: 'challenge',
            entityScreenName: screen_name,
            entityId: screen_name,
            isFreeUser: isFree,
            isDownloadingFigma: isDownloadingFigmaDesign,
            onFigmaDownload: () => {
              if (isFree) {
                figmaModal.onOpen();
                return;
              }
              onFigmaDownload();
            },
            githubLink: 'https://github.com/elise-bigdevsoon/big-dev-soon-challenge-template',
          },
        }}
      />
      <UnlockFigmaModal
        isOpen={figmaModal.isOpen}
        onClose={figmaModal.onClose}
        isDownloading={isDownloadingFigmaDesign}
        onDownload={onFigmaDownload}
      />
    </>
  );
};

export default ChallengePlayground;
